import React, { useEffect, useState, useMemo } from "react";
import { MarkdownStyled } from "@/components/AiChat/comps/Messages/AI";
import { useGenerateSummary } from "@/components/analysisModules/AnnotationExplorer/comps/Summary";
import {
  getVal,
  SANS_3,
  setVal,
  SkeletonLoader,
  TextInput,
  DropdownSingle,
  Divider,
} from "oolib";
import { useQueryClient } from "react-query";
import { WidgetHeader } from "../WidgetBarChart/WIDGET_ELEM_ConfigForm";
import { extractAllBlocksFromTpl } from "@okf/ootils";
import { qualComps } from "@/components/analysisModules/utils/qualComps";
import { blockRegistry } from "@okf/ootils";
import { useUpdateLoadedWidgetsTracker } from "../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { StyledCompWrapper } from "../../../styled";
import { WidgetErrorState } from "../../WidgetRenderer/comps/altStatePlaceholders";
import { FadeGradientWhenScroll } from "@/components/dataviz/common/FadeGradientWhenScroll";
import { getDerivedBlockTitle } from "../WidgetBarChart/utils/_extractWidgetTitle";
import LexicalTextEditor from "@/components/inputs/TextEditor/LexicalTextEditor";
import { useGetAISummary } from "../utils/useGetAISummary";
import { useReportLandingContext } from "../../../ReportLandingContext";
import { _Locale } from "@/components/locale/Locale";
import { WidgetFilters } from "../utils/WidgetFilters";
import { getWidgetContentTypes } from "../utils/getWidgetContentTypes";

export const Component = ({
  id,
  blockValuePath,
  contentType,
  activeFilters = [],
  widgetFilters = [],
  widgetIdx,
  style={},
  enableVisibilityFetching = false,
  prefetchedData,
}) => {
  const combinedFilters = useMemo(
    () => [...activeFilters, ...widgetFilters],
    [activeFilters, widgetFilters]
  );

  // Snapshot replay: prefetchedData is the previously-rendered summary string.

  const { value: summaryFromHook, status: statusFromHook, error, aiSummaryDisabled, ref } = useGetAISummary({
    originalValue: prefetchedData,
    blockValuePath,
    contentType,
    activeFilters: combinedFilters,
    enableVisibilityFetching,
    enabled: !prefetchedData,
  });

  const summary = prefetchedData || summaryFromHook;
  const status = prefetchedData ? "success" : statusFromHook;

  const { setTempWidgetConfig, updateWidgetDataStore } = useReportLandingContext?.() || {};

  // Push the rendered summary to the central store so a public snapshot can replay it.
  // Must key by `id` (widgetConfig.id) — that's the key WidgetRenderer replays prefetchedData
  // by. Replay reads this exact string back via prefetchedData.
  useEffect(() => {
    if (status === "success" && summary && id) {
      updateWidgetDataStore && updateWidgetDataStore(id, summary);
    }
  }, [status, summary, id, updateWidgetDataStore]);

  useEffect(() => {
    if(setTempWidgetConfig){
      setTempWidgetConfig((prev) => ({
        ...prev,
        [widgetIdx]: {
          ...prev[widgetIdx],
          summary,
        },
      }))
    }
  }, [ widgetIdx, summary ])

  useUpdateLoadedWidgetsTracker({status, widgetIdx, hasData: !!summary, widgetId: blockValuePath})

  const render = () => {
    if (!prefetchedData && aiSummaryDisabled) {
      return (
        <WidgetErrorState
          colorPreset={"purple"}
          display="AI Summary is disabled on this platform. Contact your admin if you wish to enable it."
        />
      );
    }

    switch (status) {
      case "loading":
        return (
          <SkeletonLoader
            style={{ width: "100%", height: "20rem", borderRadius: "0.8rem" }}
          />
        );
      case "success":
        return summary ? (
          <MarkdownStyled className="summary-markdown">{_Locale(summary)}</MarkdownStyled>
        ) : (
          <SANS_3>No Summary Found!</SANS_3>
        );

      case "error":
        return <WidgetErrorState colorPreset={"purple"} error={error || "Server Error"} />;

      default:
        return null;
    }
  };

  return (
    <StyledCompWrapper 
      ref={ref}
      style={{ ...style, 
      display: 'block',
      padding: "0 1.6rem 1.6rem" 
    }}>
      {render()}
      <FadeGradientWhenScroll style={{ position: "sticky", bottom: "-1.6rem" }}/>
    </StyledCompWrapper>
  );
};

export const optionDef = {
  display: "AI Summary",
  icon: "Stars",
  value: "aiSummary",
  comp: "WidgetAISummary",
  widgetLabel: "AI Summary",
};

export const someReqPropsUnset = (config) => {
  return !config?.props?.blockValuePath || !config?.props?.contentType;
};

export const ConfigForm = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
}) => {
  const dataKeys = {
    title: "props.title",
    blockValuePath: "props.blockValuePath",
    contentType: "props.contentType",
  };

  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");

  const widgetType = thisWidgetConfig?.comp || 'WidgetAISummary';
  const contentTypesForFilters = useMemo(() => {
    return getWidgetContentTypes(thisWidgetConfig, widgetType);
  }, [thisWidgetConfig, widgetType]);

  const contentTypeTpls = allTpls.filter(
    (tpl) => tpl.general.segment === "publishing"
  );

  // Extract blocks from all content type templates and create options
  const blockOptions = contentTypeTpls.reduce((acc, tpl) => {
    const blocks = extractAllBlocksFromTpl({ tpl });

    // Registered blocks honor instance-level qualConditions (e.g. AudioInput
    // only counts as qual when transcription.enable === true). Unregistered
    // blocks fall back to the legacy comp-name list.
    const qualBlocks = blocks
      .filter((block) =>
        blockRegistry.isRegistered(block.comp)
          ? blockRegistry.isQualBlock(block)
          : qualComps.includes(block.comp)
      )
      .map((block) => ({
        display: block.props?.label || block.props?.shortLabel || block.valuePath,
        desc: tpl.general?.content?.title || tpl.kp_content_type,
        value: `${tpl.kp_content_type}|${block.valuePath}`,
        contentType: tpl.kp_content_type,
        blockValuePath: block.valuePath,
      }));

    return [...acc, ...qualBlocks];
  }, []);

  const currentValue = (() => {
    const contentType = getVal(thisWidgetConfig, dataKeys.contentType);
    const blockValuePath = getVal(thisWidgetConfig, dataKeys.blockValuePath);
    return contentType && blockValuePath
      ? `${contentType}|${blockValuePath}`
      : "";
  })();

  const handleBlockSelection = (key, value) => {
    if (!value) return;

    const [contentType, blockValuePath] = value.split("|");

    setConfig((prev) => {
      let thisWidgetModifiedConfig = JSON.parse(
        JSON.stringify(prev[underEditWidgetIdx])
      );

      // Set both contentType and blockValuePath
      thisWidgetModifiedConfig = setVal(
        thisWidgetModifiedConfig,
        dataKeys.contentType,
        contentType
      );
      thisWidgetModifiedConfig = setVal(
        thisWidgetModifiedConfig,
        dataKeys.blockValuePath,
        blockValuePath
      );

    
      const derived = getDerivedBlockTitle({ contentType, blockValuePath });

      thisWidgetModifiedConfig = setVal(
        thisWidgetModifiedConfig,
        dataKeys.title,
        derived
      );

      const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
      modifiedFullConfig[underEditWidgetIdx] = thisWidgetModifiedConfig;
      return modifiedFullConfig;
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <WidgetHeader icon={"Stars"} title={"AI Summary"} />

      <TextInput
        id={dataKeys.title}
        label="Widget Title"
        value={getVal(thisWidgetConfig, dataKeys.title)}
        onChange={(k, v) => {
          setConfig((prev) => {
            let thisWidgetModifiedConfig = JSON.parse(
              JSON.stringify(prev[underEditWidgetIdx])
            );
            thisWidgetModifiedConfig = setVal(
              thisWidgetModifiedConfig,
              dataKeys.title,
              v
            );
            const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
            modifiedFullConfig[underEditWidgetIdx] = thisWidgetModifiedConfig;
            return modifiedFullConfig;
          });
        }}
      />

      <DropdownSingle
        id="blockSelection"
        label="Select Qualitative Data Field"
        placeholder="Choose a field to summarize"
        value={currentValue}
        onChange={handleBlockSelection}
        options={blockOptions}
        saveValueAsString
        popOutOfOverflowHiddenParent
      />

      <Divider style={{ margin: "0 -1.6rem" }}/>

      <WidgetFilters
        widgetConfig={thisWidgetConfig}
        updateWidgetConfig={setConfig}
        widgetIndex={underEditWidgetIdx}
        contentTypesForFilters={contentTypesForFilters}
      />
    </div>
  );
};

export const defaultConfig = undefined;
